import { useEffect, useMemo, useCallback } from 'react'
import {
    Box,
    Stack,
    Heading,
    Text,
    Container,
    FormControl,
    FormLabel,
    Input,
    Select,
    Button,
    FormErrorMessage,
    ButtonGroup,
    FormHelperText,
    useDisclosure,
    Avatar,
    AvatarBadge,
    Center,
    Spinner,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay
} from '@chakra-ui/react'
import * as yup from 'yup'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useNavigate, useParams } from 'react-router-dom'
import { ChildrenService } from '@/shared/api/rest/services/children.service'
import { useAlertService } from '@/shared/lib/useAlertService'
import {
    MAXIMUM_IMAGE_SIZE,
    resizeAndUploadImage
} from '@/shared/utils/imageUploadUtility'

// Define the validation schema for updating child information
const updateChildSchema = yup.object().shape({
    firstName: yup.string().min(2),
    middleName: yup.string(),
    lastName: yup.string().min(2),
    dateOfBirth: yup.date(),
    gender: yup.string(),
    allergies: yup.string(),
    bloodGroup: yup.string()
})

// Define the validation schema for uploading a picture
const pictureUploadSchema = yup.object().shape({
    picture: yup
        .mixed()
        .test('noImage', 'Image is required', (value: any) => {
            return value && value[0]?.size
        })
        .test('fileSize', 'The file is too large', (value: any) => {
            return value && value[0].size <= MAXIMUM_IMAGE_SIZE
        })
})

export default function EditChild() {
    const alertService = useAlertService()
    const navigate = useNavigate()
    const { id = '' } = useParams<{ id: string }>()
    const { isOpen, onOpen, onClose } = useDisclosure()

    const {
        data: childData,
        isLoading: loading,
        error
    } = useQuery({
        queryKey: ['child', id],
        enabled: !!id,
        queryFn: () => ChildrenService.findById(id)
    })

    // hook from react-hook-form for handling form state and validation
    const {
        register,
        handleSubmit,
        reset,
        setValue,
        formState: { errors, isSubmitting }
    } = useForm({
        resolver: yupResolver(updateChildSchema),
        defaultValues: useMemo(
            () => ({
                firstName: '',
                middleName: '',
                lastName: '',
                dateOfBirth: new Date(),
                allergies: '',
                gender: '',
                bloodGroup: ''
            }),
            []
        )
    })

    // REST mutation for updating child information. Keeps the Apollo-style
    // call shape `updateChild({ variables: { id, data } })` used below.
    const { mutateAsync: updateChildMutation } = useMutation({
        mutationFn: (vars: { id: string; data: Record<string, unknown> }) =>
            ChildrenService.update(vars.id, vars.data),
        onSuccess: () => {
            alertService.success('Child updated successfully')
            navigate('/user/parent/children-list')
        },
        onError: (error: Error) => {
            alertService.error(error.message, error.name)
        }
    })
    const updateChild = (args: {
        variables: { id: string; data: Record<string, unknown> }
    }) => updateChildMutation(args.variables)

    // Separate useForm hook for handling picture upload
    const {
        register: registerPics,
        handleSubmit: handleSubmitPics,
        formState: { errors: picErrors, isSubmitting: isSubmittingPics }
    } = useForm({
        resolver: yupResolver(pictureUploadSchema),
        defaultValues: { picture: '' }
    })

    // Handler for submitting the picture upload form
    const onSubmitPics = useCallback(
        async (formValue: any) => {
            try {
                const imageUrl = await resizeAndUploadImage(formValue, '')
                await updateChild({
                    variables: {
                        id,
                        data: {
                            picture: imageUrl
                        }
                    }
                })
                onClose()
            } catch (error: any) {
                alertService.error(error.message, error.name)
            }
        },
        [updateChild, onClose, alertService, id]
    )

    const onSubmit = useCallback(
        async (formValue: any) => {
            try {
                // Backend expects `allergies` as a string[]; the form holds a
                // single string, so split it before sending.
                const allergies =
                    typeof formValue.allergies === 'string'
                        ? formValue.allergies
                              .split(',')
                              .map((a: string) => a.trim())
                              .filter(Boolean)
                        : formValue.allergies
                await updateChild({
                    variables: {
                        id,
                        data: {
                            ...formValue,
                            allergies
                        }
                    }
                })
                reset()
            } catch (error: any) {
                alertService.error(error.message, error.name)
            }
        },
        [updateChild, reset, alertService, id]
    )

    // Populate form fields with fetched data
    useEffect(() => {
        if (childData) {
            const child = childData
            const dateOfBirth = child.dateOfBirth
                ? new Date(child.dateOfBirth).toISOString().substring(0, 10)
                : ''

            // Set the form values
            setValue('firstName', child.firstName)
            setValue('lastName', child.lastName)
            // @ts-expect-error - dateOfBirth is a string and to set it to a date input, it needs to be a string
            setValue('dateOfBirth', dateOfBirth)
            // allergies is a string[] on the backend; the form holds a single
            // comma-separated string (split again on submit). Join ALL allergies
            // so editing doesn't silently drop everything past the first.
            setValue('allergies', child.allergies?.join(', ') ?? '')
            setValue('gender', child.gender ?? '')
            setValue('bloodGroup', child.bloodGroup ?? '')
        }
    }, [childData, setValue])

    // Show a loading spinner while data is being fetched
    if (loading)
        return (
            <Center>
                <Spinner size="xl" />
            </Center>
        )

    // Show an error message if there was an error fetching data
    if (error) return <Center>Error: {error.message}</Center>

    return (
        <>
            <Container>
                <Stack textAlign={'center'} mb="4">
                    <Heading fontSize={'2xl'}>Edit Child</Heading>
                    <Text fontSize={{ base: 'sm', sm: 'md' }} color={'gray.600'}>
                        Update your child information
                    </Text>
                </Stack>

                <Box rounded={'lg'} bg={'white'} boxShadow={'lg'} p={8}>
                    <Center p={6} mb={2}>
                        <Avatar
                            bg="brand.100"
                            size="xl"
                            boxSize="3em"
                            src={childData?.picture ?? undefined}
                        >
                            <AvatarBadge as={Button} bg="brand" onClick={onOpen}>
                                Edit
                            </AvatarBadge>
                        </Avatar>
                    </Center>
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <Stack spacing={4}>
                            <FormControl isInvalid={Boolean(errors.firstName)}>
                                <FormLabel>First Name</FormLabel>
                                <Input
                                    type="text"
                                    id="firstName"
                                    {...register('firstName')}
                                />
                                <FormErrorMessage>
                                    {errors.firstName && errors.firstName.message}
                                </FormErrorMessage>
                            </FormControl>
                            <FormControl isInvalid={Boolean(errors.middleName)}>
                                <FormLabel>Middle Name</FormLabel>
                                <Input
                                    type="text"
                                    id="middleName"
                                    {...register('middleName')}
                                />
                                <FormErrorMessage>
                                    {errors.middleName && errors.middleName.message}
                                </FormErrorMessage>
                            </FormControl>
                            <FormControl isInvalid={Boolean(errors.lastName)}>
                                <FormLabel>Surname</FormLabel>
                                <Input
                                    type="text"
                                    id="lastName"
                                    {...register('lastName')}
                                />
                                <FormErrorMessage>
                                    {errors.lastName && errors.lastName.message}
                                </FormErrorMessage>
                            </FormControl>
                            <FormControl isInvalid={Boolean(errors.dateOfBirth)}>
                                <FormLabel>Date of Birth</FormLabel>
                                <Input
                                    type="date"
                                    id="dateOfBirth"
                                    {...register('dateOfBirth')}
                                />
                                <FormErrorMessage>
                                    {errors.dateOfBirth && errors.dateOfBirth.message}
                                </FormErrorMessage>
                            </FormControl>
                            <FormControl isInvalid={Boolean(errors.gender)}>
                                <FormLabel>Gender</FormLabel>
                                <Select
                                    id="gender"
                                    placeholder="Select Gender"
                                    {...register('gender')}
                                >
                                    <option value="male">Male</option>
                                    <option value="female">Female</option>
                                </Select>
                                <FormErrorMessage>
                                    {errors.gender && errors.gender.message}
                                </FormErrorMessage>
                            </FormControl>

                            <FormControl isInvalid={Boolean(errors.bloodGroup)}>
                                <FormLabel>Blood Group</FormLabel>
                                <Select
                                    id="bloodGroup"
                                    placeholder="Select Blood Group"
                                    {...register('bloodGroup')}
                                >
                                    <option value="A +">A+</option>
                                    <option value="A -">A-</option>
                                    <option value="B +">B+</option>
                                    <option value="B -">B-</option>
                                    <option value="O +">O+</option>
                                    <option value="O -">O-</option>
                                    <option value="AB +">AB+</option>
                                    <option value="AB -">AB-</option>
                                </Select>
                                <FormErrorMessage>
                                    {errors.bloodGroup && errors.bloodGroup.message}
                                </FormErrorMessage>
                            </FormControl>

                            <FormControl isInvalid={Boolean(errors.allergies)}>
                                <FormLabel>Allergies</FormLabel>
                                <Input
                                    type="text"
                                    id="allergies"
                                    {...register('allergies')}
                                />
                                <FormErrorMessage>
                                    {errors.allergies && errors.allergies.message}
                                </FormErrorMessage>
                            </FormControl>
                            <ButtonGroup gap="4" mt={4}>
                                <Button
                                    type="button"
                                    size="lg"
                                    variant="outline"
                                    onClick={() =>
                                        navigate('/user/parent/children-list')
                                    }
                                >
                                    Cancel
                                </Button>
                                <Button
                                    w={'full'}
                                    type="submit"
                                    size="lg"
                                    isLoading={isSubmitting}
                                    loadingText="Submitting"
                                    onClick={handleSubmit(onSubmit)}
                                >
                                    Save
                                </Button>
                            </ButtonGroup>
                        </Stack>
                    </form>
                </Box>

                <Modal isOpen={isOpen} onClose={onClose}>
                    <ModalOverlay />
                    <ModalContent>
                        <ModalHeader>Edit Picture</ModalHeader>
                        <ModalCloseButton />
                        <form onSubmit={handleSubmitPics(onSubmitPics)}>
                            <ModalBody>
                                <FormControl isInvalid={Boolean(picErrors.picture)}>
                                    <FormLabel>Picture</FormLabel>
                                    <Input
                                        type="file"
                                        id="picture"
                                        accept="image/*"
                                        {...registerPics('picture')}
                                    />
                                    <FormErrorMessage>
                                        {picErrors.picture && picErrors.picture.message}
                                    </FormErrorMessage>
                                    <FormHelperText>
                                        Maximum image size allowed is 4MB 😉
                                    </FormHelperText>
                                </FormControl>
                            </ModalBody>
                            <ModalFooter>
                                <ButtonGroup gap="4" mt={4}>
                                    <Button
                                        type="button"
                                        size="lg"
                                        variant="outline"
                                        onClick={onClose}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        w={'full'}
                                        type="submit"
                                        size="lg"
                                        isLoading={isSubmittingPics}
                                        loadingText="Submitting"
                                    >
                                        Save
                                    </Button>
                                </ButtonGroup>
                            </ModalFooter>
                        </form>
                    </ModalContent>
                </Modal>
            </Container>
        </>
    )
}
